import { useEffect } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import { GlobalHomePage } from '../components/index/GlobalHomePage'
import { HandleTinyUrl } from '../components/index/HandleTinyUrl'
import { legalName, socialProfiles } from '../utils/business-info'
import { COUNTRY_HOME_ALTERNATES } from '../utils/markets'
import { buildAirconPriceTables } from '../js/aircon-cost'
import { lowestSessionPrice } from '../components/market/PtPanels'

// Global page for StringsSG in Singapore, the UAE and Malaysia. Until Oct 2026
// "/" was the Singapore homepage; that content now lives at /sg
// (pages/sg/index.js). This page links to the three country homes, is their
// hreflang x-default, and never redirects (see GlobalHomePage).
//
// Things that must stay on "/":
//  - HandleTinyUrl: backend emails and SMS link to /?tinyUrlId= and
//    /?directLoginId=, generated outside this repo.
//  - The Google and Facebook domain verification tags.
//  - The Organization and WebSite JSON-LD nodes, whose @ids ("/#organization",
//    "/#website") are referenced from pages across the site.

const SITE = "https://www.stringssg.com"
const OG_IMAGE = "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/girl-with-stringssg-logo-on-mobile.jpg"

// Static - must not be new Date(), which differs between server render and
// hydration and claims the page changed on every build.
const LAST_MODIFIED = "2026-10-04T00:00:00+08:00"

const TITLE = "StringsSG | Aircon Service & Personal Trainers in Singapore, UAE & Malaysia"
const DESCRIPTION = "Home services since 2018: aircon servicing and certified personal trainers in Singapore, Dubai, Abu Dhabi and Kuala Lumpur. Choose your country for local prices."

const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "Organization",
            "@id": `${SITE}/#organization`,
            "name": legalName,
            "alternateName": "StringsSG",
            "url": `${SITE}/`,
            "logo": {
                "@type": "ImageObject",
                "url": "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png",
                "width": 200,
                "height": 60,
            },
            "foundingDate": "2018",
            "description": "Home services company founded in Singapore in 2018, offering aircon servicing and in-house personal trainers in Singapore, the United Arab Emirates and Malaysia, plus handyman and helper services in Singapore.",
            "slogan": "Professional Home Services, Delivered to StringsSG Standards",
            "sameAs": [...socialProfiles, "https://x.com/stringssg"],
            "areaServed": [
                { "@type": "Country", "name": "Singapore" },
                { "@type": "Country", "name": "United Arab Emirates" },
                { "@type": "Country", "name": "Malaysia" },
            ],
            "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+65-6680-3926",
                "contactType": "customer service",
                "areaServed": "SG",
                "availableLanguage": ["English", "Chinese"],
            },
            "subOrganization": { "@id": `${SITE}/#localbusiness` },
        },
        {
            "@type": "WebSite",
            "@id": `${SITE}/#website`,
            "url": `${SITE}/`,
            "name": "StringsSG",
            "publisher": { "@id": `${SITE}/#organization` },
            "description": "StringsSG home services in Singapore, the United Arab Emirates and Malaysia.",
            "inLanguage": "en",
        },
        {
            "@type": "WebPage",
            "@id": `${SITE}/#webpage`,
            "url": `${SITE}/`,
            "name": TITLE,
            "description": DESCRIPTION,
            "isPartOf": { "@id": `${SITE}/#website` },
            "about": { "@id": `${SITE}/#organization` },
            "inLanguage": "en",
            "datePublished": "2018-01-01T00:00:00+08:00",
            "dateModified": LAST_MODIFIED,
        },
    ],
}

// "From" prices for the UAE and Malaysia country cards, from the same pricing
// profiles as those countries' pages (Singapore's are fixed copy, as on /sg).
export const getStaticProps = () => ({
    props: {
        prices: Object.fromEntries(['ae', 'my'].map((cd) => [cd, {
            aircon: buildAirconPriceTables(cd).general[0].wall,
            pt: lowestSessionPrice(cd),
        }])),
    },
    revalidate: 259200,
})

export default function Home({ prices }) {

    useEffect(() => {
        const currentLocation = globalThis.location.href;
        if (currentLocation.includes("#")){
            let locationId = currentLocation.substring(currentLocation.indexOf("#") + 1);
            let ele = document.getElementById(locationId);
            if(ele){
                setTimeout(() => {
                    ele.scrollIntoView({ behavior: "smooth", block:"start"});
                }, 500)
            }
        }
    }, [])

  return (
    <>
      <Head>
        <title>{TITLE}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={DESCRIPTION}/>
        <meta name="author" content={legalName}/>
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta httpEquiv="content-language" content="en" />

        {/* Google Verification */}
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta name="facebook-domain-verification" content="kpyzbr03lkk332k1tjykmltrvnsvvs" />

        {/* Open Graph */}
        <meta property="og:locale" content="en_SG" />
        <meta property="og:locale:alternate" content="en_AE" />
        <meta property="og:locale:alternate" content="en_MY" />
        <meta property="og:site_name" content="StringsSG" />
        <meta property="og:url" content={`${SITE}/`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={TITLE}/>
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:secure_url" content={OG_IMAGE} />
        <meta property="og:image:alt" content="StringsSG home services in Singapore, the UAE and Malaysia" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:creator" content="@stringssg"/>
        <meta name="twitter:url" content={`${SITE}/`}/>
        <meta name="twitter:title" content={TITLE}/>
        <meta name="twitter:description" content={DESCRIPTION}/>
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content="StringsSG home services in Singapore, the UAE and Malaysia" />

        <link rel="icon" href="/favicon.ico" />

        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchema) }}
        />

        <link rel="canonical" href={`${SITE}/`} />
        {COUNTRY_HOME_ALTERNATES.map((alt) => (
          <link key={alt.hreflang} rel="alternate" hrefLang={alt.hreflang} href={alt.path === '/' ? `${SITE}/` : `${SITE}${alt.path}`} />
        ))}
      </Head>

      <HandleTinyUrl/>
      <GlobalHomePage prices={prices} />

      {/* Google Analytics */}
      <Script
        id="google-tagmanager-new"
        src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
        strategy="lazyOnload"
        onLoad={() => {
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-QD1HGGHDYJ');
        }}
      />

      {/* Facebook Pixel is loaded globally in _app.js (id="facebook-tracking") -
          it was duplicated here with the same script id, double-firing PageView. */}
    </>
  )
}
